import { injectLocal, provideLocal } from '@vueuse/core';
import { computed, ref, watch, type InjectionKey, type Ref } from 'vue';
import type { Notification, NotificationType } from '@/types/models';
import { useFilters } from './useFilters';
import {
    notificationTypeEnum,
    notificationTypeKeyMap,
} from '@/enums/notifications';
import { useGet, usePost } from '@foodics/ui-common';
import {
    notificationsApi,
    type CountByTypeNotificationsRequest,
    type ListNotificationsRequest,
    type MarkAllAsReadNotificationRequest,
} from '@/api/notifications';
import { resolvePaymentPartyName } from './payments';
import { useLocale } from './locale';
import { cloneDeep, isEmpty, sum } from 'lodash-es';
import { getCommaSeparatedNumber } from '@/utils/number';
import { resolveTransactionPartyName } from './transactions';
import { useRealtime } from './useRealtime';
import { useCurrentUser } from './currentUser';
import { usePagination } from './usePagination';

type NotificationFilters = {
    type?: NotificationType[];
};

export type NotificationGroup = 'transactions' | 'for_you';
export const groupTypesMap: Record<NotificationGroup, NotificationType[]> = {
    transactions: [
        notificationTypeEnum.OUTGOING_TRANSFER_SUCCESSFUL,
        notificationTypeEnum.FOODICS_PAY_DAILY_SUMMARY,
        notificationTypeEnum.INCOMING_TRANSFER,
        notificationTypeEnum.SADAD_BILL_PAID,
        notificationTypeEnum.COLLECTION_SUCCESSFUL,
    ],
    for_you: [
        notificationTypeEnum.NEW_MEMBER_JOINED,
        notificationTypeEnum.TRANSACTIONS_EXPORT_READY,
        notificationTypeEnum.APPROVAL_RULE_UPDATED,
    ],
};

const useNotificationsInjectionKey: InjectionKey<UseNotificationsContext> =
    Symbol('useNotificationsInjectionKey');

type UseNotificationsContext = {
    notifications: Ref<Notification[]>;
    appliedFilters: Ref<NotificationFilters>;
    setFilter: (
        filterKey: keyof NotificationFilters,
        filterValue: NotificationFilters[keyof NotificationFilters],
    ) => void;
    resetFilters: () => void;
    unreadCount: Ref<number>;
    hasUnread: Ref<boolean>;
    isLoading: Ref<boolean>;
    markAllAsRead: () => Promise<void>;
    getUnreadCountByType: (
        types: NotificationType | NotificationType[],
    ) => number;
    hasNextPage: Ref<boolean>;
    fetchNextPage: () => void;
    hasFetchedInitialPage: Ref<boolean>;
    resetPagination: () => void;
    isLoadingUnreadCount: Ref<boolean>;
};

export const useNotifications = (opts?: { filters?: NotificationFilters }) => {
    const prevCtx = injectLocal(useNotificationsInjectionKey);
    // once we pass new options, we create a new context
    if (prevCtx && isEmpty(opts)) {
        return prevCtx;
    }

    // Use filters for category filtering
    const { appliedFilters, setFilter, resetFilters } =
        useFilters<NotificationFilters>({
            ignoreQueryParams: true,
            initialValues: opts?.filters,
        });

    const {
        items,
        isLoading,
        hasNextPage,
        fetchNextPage,
        hasFetchedInitialPage,
        resetPagination,
    } = usePagination<ListNotificationsRequest>(notificationsApi.list, {
        fetchOptions: computed(() => ({
            params: {
                filter: appliedFilters.value,
            },
        })),
    });

    const notifications = ref<Notification[]>([]);
    watch(items, () => {
        const existingNotificationsMap = new Map(
            notifications.value.map((n) => [n.id, n]),
        );
        // TODO: until we use the logic from the library
        const newNotifications = items.value.filter(
            (n) => !existingNotificationsMap.has(n.id),
        );
        notifications.value.push(...newNotifications);
    });

    watch(
        appliedFilters,
        () => {
            notifications.value = [];
            resetPagination();
        },
        { deep: true },
    );

    const { data: unreadCountByTypeData, isLoading: isLoadingUnreadCount } =
        useGet<CountByTypeNotificationsRequest['response']>(
            notificationsApi.countByType,
        );

    const { execute: executeMarkAllAsRead } = usePost<
        MarkAllAsReadNotificationRequest['payload'],
        MarkAllAsReadNotificationRequest['response']
    >(notificationsApi.markAllAsRead);

    // Total unread count across all notifications
    const unreadCount = computed(() => {
        if (!unreadCountByTypeData.value) return 0;
        return sum(Object.values(unreadCountByTypeData.value));
    });

    const hasUnread = computed(() => unreadCount.value > 0);

    const getUnreadCountByType = (
        types: NotificationType | NotificationType[],
    ) => {
        const data = unreadCountByTypeData.value;
        if (!data) return 0;

        const arr = Array.isArray(types) ? types : [types];
        return arr.reduce((acc, t) => acc + (data[t] ?? 0), 0);
    };
    // Mark all notifications as read
    const markAllAsRead = async () => {
        const filteredTypes = appliedFilters.value?.type;
        const unreadForCurrentFilters = filteredTypes
            ? getUnreadCountByType(filteredTypes)
            : unreadCount.value;

        // if no unread notifications, don't call the API
        if (unreadForCurrentFilters === 0) return;

        const unreadCountsBackup = cloneDeep(unreadCountByTypeData.value);
        const notificationsBackup = cloneDeep(notifications.value);

        // updating the unread count
        const counts = unreadCountByTypeData.value;
        if (counts) {
            const typesToZero = filteredTypes ?? Object.keys(counts);
            for (const type of typesToZero) {
                counts[type as NotificationType] = 0;
            }
        }

        // updating the notifications data
        notifications.value.forEach((n) => {
            n.read_at = new Date().toISOString();
        });

        // execute the API call to mark all as read
        const { error } = await executeMarkAllAsRead({
            types: appliedFilters.value?.type,
        });

        if (error) {
            // revert the optimistic changes
            unreadCountByTypeData.value = unreadCountsBackup;
            notifications.value = notificationsBackup;
            return;
        }
    };

    const { usePrivateChannel } = useRealtime();
    const currentUser = useCurrentUser();

    usePrivateChannel<Notification>(
        `notifications.${currentUser.user.value?.id}`,
        '.notification.created',
        (newNotification) => {
            const notificationType = newNotification.type;

            // update the unread count
            if (unreadCountByTypeData.value) {
                unreadCountByTypeData.value[notificationType] =
                    (unreadCountByTypeData.value[notificationType] ?? 0) + 1;
            }

            // not interested in this notification
            if (
                appliedFilters.value.type &&
                !appliedFilters.value.type.includes(notificationType)
            )
                return;

            // check if the notification already exists
            const existingNotification = notifications.value.find(
                (n) => n.id === newNotification.id,
            );
            if (existingNotification) return;

            // add the notification to the list at the beginning as new notifications are at the end of the list
            notifications.value.unshift(newNotification);
        },
    );

    const ctx: UseNotificationsContext = {
        notifications,
        appliedFilters,
        setFilter,
        resetFilters,
        unreadCount,
        hasUnread,
        isLoading,
        markAllAsRead,
        getUnreadCountByType,
        hasNextPage,
        fetchNextPage,
        hasFetchedInitialPage,
        resetPagination,
        isLoadingUnreadCount,
    };

    provideLocal(useNotificationsInjectionKey, ctx);

    return ctx;
};

export function constructNotificationTitle(notification: Notification) {
    const { t } = useLocale();
    return t(
        `notifications.types.${notificationTypeKeyMap[notification.type]}.title`,
    );
}

export function constructNotificationBody(notification: Notification) {
    const { t } = useLocale();

    return t(
        `notifications.types.${notificationTypeKeyMap[notification.type]}.description`,
        constructNotificationBodyParams(notification),
    );
}

function constructNotificationBodyParams(notification: Notification): string[] {
    switch (notification.type) {
        case notificationTypeEnum.NEW_MEMBER_JOINED:
            return [notification.data.source.name];
        case notificationTypeEnum.OUTGOING_TRANSFER_SUCCESSFUL:
            return [
                getCommaSeparatedNumber(
                    Math.abs(notification.data.source.amount.value),
                ),
                resolvePaymentPartyName(notification.data.source),
            ];
        case notificationTypeEnum.FOODICS_PAY_DAILY_SUMMARY:
            return [
                getCommaSeparatedNumber(Math.abs(notification.data.total)),
                String(notification.data.transaction_count),
            ];
        case notificationTypeEnum.INCOMING_TRANSFER:
            return [
                getCommaSeparatedNumber(
                    Math.abs(notification.data.source.amount.value),
                ),
                resolveTransactionPartyName(notification.data.source),
            ];
        case notificationTypeEnum.SADAD_BILL_PAID:
            return [
                getCommaSeparatedNumber(
                    Math.abs(notification.data.source.amount.value),
                ),
            ];
        case notificationTypeEnum.COLLECTION_SUCCESSFUL:
            return [
                getCommaSeparatedNumber(
                    Math.abs(notification.data.source.amount.value),
                ),
            ];
    }

    return [];
}
